import { getApiClient } from "@src/api";
import {
	invalidateNavbarBranchSnapshots,
	optimisticallyRemoveNavbarItem,
	restoreNavbarBranchSnapshots,
} from "@src/components/board/mutations/navbar-optimistic";
import { navbarBranchQueryKeys } from "@src/components/board/queries/fetch-ranklist";
import { parseChatDocumentId } from "@src/components/collaboration/document-id";
import { graphql } from "@src/gql";
import type { NavbarSection } from "@src/gql/graphql";
import { handleError } from "@src/utils/error-handling";
import { showSuccessNotification } from "@src/utils/notifications";
import { useMutation, useQueryClient } from "@tanstack/react-query";

export const navbarRemoveChatMutation = graphql(/* GraphQL */ `
	mutation navbarRemoveChat(
		$section: NavbarSection!
		$chatId: String!
		$waitForProjections: Boolean!
	) {
		navbar {
			removeChat(
				section: $section
				chatId: $chatId
				waitForProjections: $waitForProjections
			)
		}
	}
`);

export type NavbarRemoveChatInput = {
	section: NavbarSection;
	chatId: string;
	waitForProjections?: boolean;
};

/**
 * Unpin an Agent chat link from a navbar section (My Workspace).
 * GraphQL: `navbar.removeChat`.
 */
export function useNavbarRemoveChat(showNotification = true) {
	const client = getApiClient();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async ({
			section,
			chatId,
			waitForProjections,
		}: NavbarRemoveChatInput) => {
			const response = await client.mutate({
				mutation: navbarRemoveChatMutation,
				variables: {
					section,
					chatId: parseChatDocumentId(chatId).toString(),
					waitForProjections: waitForProjections ?? false,
				},
			});
			return response.data?.navbar?.removeChat ?? false;
		},
		onMutate: async ({ chatId }) => {
			const chatKey = parseChatDocumentId(chatId).toString();
			const snapshots = await optimisticallyRemoveNavbarItem(
				queryClient,
				chatKey,
			);
			return { snapshots };
		},
		onSuccess: () => {
			if (showNotification) {
				showSuccessNotification("Success", "Unpinned from Workspace");
			}
		},
		onError: (error, _vars, context) => {
			if (context?.snapshots) {
				restoreNavbarBranchSnapshots(queryClient, context.snapshots);
			}
			handleError(error, "Failed to unpin chat from Workspace");
		},
		onSettled: (_data, _error, _vars, context) => {
			invalidateNavbarBranchSnapshots(queryClient, context?.snapshots ?? []);
			void queryClient.invalidateQueries({
				queryKey: navbarBranchQueryKeys.all(),
			});
		},
	});
}
